export type AttachSessionKind = 'media' | 'document';

export function getAttachSessionKind(file: File): AttachSessionKind {
  if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
    return 'media';
  }
  return 'document';
}

export function filterFilesForSession(
  files: File[],
  kind: AttachSessionKind
): { accepted: File[]; rejected: File[] } {
  const accepted: File[] = [];
  const rejected: File[] = [];

  for (const file of files) {
    if (getAttachSessionKind(file) === kind) {
      accepted.push(file);
    } else {
      rejected.push(file);
    }
  }

  return { accepted, rejected };
}

export function assertSameSession(
  files: File[],
  kind: AttachSessionKind
): File[] {
  return filterFilesForSession(files, kind).accepted;
}

export function inferPasteSessionKind(files: File[]): AttachSessionKind | null {
  if (files.length === 0) return null;
  return getAttachSessionKind(files[0]);
}

export function resolvePasteFiles(files: File[]): {
  kind: AttachSessionKind;
  accepted: File[];
  rejected: File[];
} | null {
  const kind = inferPasteSessionKind(files);
  if (!kind) return null;
  const { accepted, rejected } = filterFilesForSession(files, kind);
  return { kind, accepted, rejected };
}

/**
 * Paste/drop → sessão de anexo.
 * `mediaOnly` (IG Direct): mesmo recorte do menu + (só foto/vídeo).
 */
export function resolveIncomingAttachFiles(
  files: File[],
  options?: { mediaOnly?: boolean },
): { kind: AttachSessionKind; accepted: File[]; rejected: File[] } | null {
  if (files.length === 0) return null;
  if (options?.mediaOnly) {
    const { accepted, rejected } = filterFilesForSession(files, 'media');
    return { kind: 'media', accepted, rejected };
  }
  return resolvePasteFiles(files);
}

/** Arquivos de um drop (DataTransfer) — mesma regra de sessão do paste. */
export function extractDataTransferFiles(
  dataTransfer: DataTransfer | null | undefined,
): File[] {
  if (!dataTransfer?.files?.length) return [];
  return Array.from(dataTransfer.files);
}

export function dataTransferHasFiles(
  dataTransfer: DataTransfer | null | undefined,
): boolean {
  if (!dataTransfer) return false;
  if (typeof dataTransfer.types !== 'undefined') {
    return Array.from(dataTransfer.types).includes('Files');
  }
  return Boolean(dataTransfer.files?.length);
}

export function isCrossKindAttachSession(
  existingKind: AttachSessionKind | null,
  newKind: AttachSessionKind,
  hasStagedItems: boolean
): boolean {
  return hasStagedItems && existingKind !== null && existingKind !== newKind;
}

export function partitionValidFileSizes(
  files: File[],
  assertSize: (file: File) => void
): { valid: File[]; invalid: { file: File; message: string }[] } {
  const valid: File[] = [];
  const invalid: { file: File; message: string }[] = [];

  for (const file of files) {
    try {
      assertSize(file);
      valid.push(file);
    } catch (err) {
      invalid.push({
        file,
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }

  return { valid, invalid };
}
